import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { ApiBaseUrls, ApiControllers } from "../../constants/constants";
import { ServiceResultModel } from "../../models/service/service-result.model";
import { ServiceUtilities } from "../../utilities/service.utilities";
import { CurrencyModel } from "../../models/data/currency.model";
import { CreateExpenseRequest, ExpenseModel, UpdateExpenseRequest, VoidExpenseRequest } from "../../models/data/expense.model";
import { CreateManualLedgerEntriesRequest, GeneralLedgerEntryModel } from "../../models/data/ledger-entry.model";
import { TrialBalanceReportModel } from "../../models/data/trial-balance-report.model";
import { BalanceSheetReportModel } from "../../models/data/balance-sheet-report.model";
import { IncomeStatementReportModel } from "../../models/data/income-statement-report.model";
import { APISanitizerUtilities } from "../../utilities/api-sanitizer.utilities";
import { BudgetModel, CreateBudgetRequest, UpdateBudgetRequest } from "../../models/data/budget.model";
import { BudgetSummaryModel } from "../../models/data/budget-summary.model";
import { BudgetAuditLogModel } from "../../models/data/budget-audit-log.model";
import { BudgetsFilter } from "../../filters/budgets.filter";
import { ExpenseFilter } from "../../filters/expense.filter";
import { PaginatedListModel } from "../../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../../models/service/pagination-response-header.model";
import { AuditLog } from "../../models/service/audit-log.model";
import { AuditLogMapper } from "../../utilities/audit-log-mapper.utilities";
import { BalancesMessagingConstants, BudgetMessagingConstants } from "../../constants/messaging.constants";

@Injectable({
  providedIn: 'root'
})
export class FinancialService {
  private financialsApiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.FINANCIALS()}`;

  constructor(private httpClient: HttpClient) { }

  public async getCurrencies(): Promise<ServiceResultModel<CurrencyModel[]>> {
    try {
      const requestUrl = `${this.financialsApiPath}/currencies`;
      const response = await firstValueFrom(
        this.httpClient.get<CurrencyModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const currencies = <CurrencyModel[]>response.body?.valueOf();
      return <ServiceResultModel>{ data: currencies, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createManualLedgerEntries(request: CreateManualLedgerEntriesRequest): Promise<ServiceResultModel<GeneralLedgerEntryModel[]>> {
    try {
      const requestUrl = `${this.financialsApiPath}/ledger-entries`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.post<GeneralLedgerEntryModel[]>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const entries = <GeneralLedgerEntryModel[]>response.body?.valueOf();
      return <ServiceResultModel<GeneralLedgerEntryModel[]>>{ data: entries, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createExpense(request: CreateExpenseRequest): Promise<ServiceResultModel<ExpenseModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/expenses`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.post<ExpenseModel>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const expense = <ExpenseModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: expense, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateExpense(expenseId: number, request: UpdateExpenseRequest): Promise<ServiceResultModel<ExpenseModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/expenses/${expenseId}`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.patch<ExpenseModel>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const expense = <ExpenseModel>response.body?.valueOf();
      return <ServiceResultModel<ExpenseModel>>{
        data: expense,
        success: true,
        message: BalancesMessagingConstants.EXPENSE_UPDATE_SUCCESS(),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async voidExpense(expenseId: number, note?: string): Promise<ServiceResultModel<ExpenseModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/expenses/${expenseId}/void`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload<VoidExpenseRequest>({ note: note ?? null });
      const response = await firstValueFrom(
        this.httpClient.patch<ExpenseModel>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const expense = <ExpenseModel>response.body?.valueOf();
      return <ServiceResultModel<ExpenseModel>>{
        data: expense,
        success: true,
        message: BalancesMessagingConstants.EXPENSE_VOID_SUCCESS(),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchExpenses(
    filter: ExpenseFilter,
    orderBy?: string,
    skip?: number,
    take?: number
  ): Promise<ServiceResultModel<PaginatedListModel<ExpenseModel>>> {
    try {
      const encodedOrderBy = orderBy ? encodeURIComponent(orderBy) : '';
      const requestUrl = `${this.financialsApiPath}/expenses/filter?orderBy=${encodedOrderBy}&skip=${skip ?? 0}&take=${take ?? ''}`;

      const response = await firstValueFrom(
        this.httpClient.post<ExpenseModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<ExpenseFilter>(filter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<ExpenseModel> = {
        data: <ExpenseModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getTrialBalance(
    organizationId: number,
    asOfDate: string
  ): Promise<ServiceResultModel<TrialBalanceReportModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/${organizationId}/trial-balance?asOfDate=${encodeURIComponent(asOfDate)}`;
      const response = await firstValueFrom(
        this.httpClient.get<TrialBalanceReportModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const report = <TrialBalanceReportModel>response.body?.valueOf();
      return <ServiceResultModel<TrialBalanceReportModel>>{ data: report, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getBalanceSheet(
    organizationId: number,
    asOfDate: string
  ): Promise<ServiceResultModel<BalanceSheetReportModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/${organizationId}/balance-sheet?asOfDate=${encodeURIComponent(asOfDate)}`;
      const response = await firstValueFrom(
        this.httpClient.get<BalanceSheetReportModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const report = <BalanceSheetReportModel>response.body?.valueOf();
      return <ServiceResultModel<BalanceSheetReportModel>>{ data: report, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getIncomeStatement(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Promise<ServiceResultModel<IncomeStatementReportModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/${organizationId}/income-statement?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
      const response = await firstValueFrom(
        this.httpClient.get<IncomeStatementReportModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const report = <IncomeStatementReportModel>response.body?.valueOf();
      return <ServiceResultModel<IncomeStatementReportModel>>{ data: report, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createBudget(request: CreateBudgetRequest): Promise<ServiceResultModel<BudgetModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/budgets`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.post<BudgetModel>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const budget = <BudgetModel>response.body?.valueOf();
      return <ServiceResultModel<BudgetModel>>{ data: budget, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getBudgetById(budgetId: number): Promise<ServiceResultModel<BudgetModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/budgets/${budgetId}`;
      const response = await firstValueFrom(
        this.httpClient.get<BudgetModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const budget = <BudgetModel>response.body?.valueOf();
      return <ServiceResultModel<BudgetModel>>{ data: budget, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateBudget(budgetId: number, request: UpdateBudgetRequest): Promise<ServiceResultModel<BudgetModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/budgets/${budgetId}`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.patch<BudgetModel>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const budget = <BudgetModel>response.body?.valueOf();
      return <ServiceResultModel<BudgetModel>>{ data: budget, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async deleteBudget(budgetId: number, note?: string): Promise<ServiceResultModel<BudgetModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/budgets/${budgetId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<BudgetModel>(
          requestUrl,
          {
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
            observe: "response",
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          }
        )
      );

      const budget = <BudgetModel>response.body?.valueOf();
      const successMessage = BudgetMessagingConstants.BUDGET_DELETE_SUCCESS()
        .replaceValuesWithObject({ name: budget?.name ?? '' });

      return <ServiceResultModel<BudgetModel>>{
        data: budget,
        success: true,
        message: successMessage,
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getAuditLogsByBudgetId(
    budgetId: number,
    skip: number,
    take: number,
    includeDetails: boolean = false
  ): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = `${this.financialsApiPath}/budgets/${budgetId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(
        this.httpClient.get<BudgetAuditLogModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const mappedData = AuditLogMapper.mapBudgetAuditLogs(<BudgetAuditLogModel[]>response.body?.valueOf());

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getBudgetSummaryById(budgetId: number): Promise<ServiceResultModel<BudgetSummaryModel>> {
    try {
      const requestUrl = `${this.financialsApiPath}/budgets/${budgetId}/summary`;
      const response = await firstValueFrom(
        this.httpClient.get<BudgetSummaryModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const summary = <BudgetSummaryModel>response.body?.valueOf();
      return <ServiceResultModel<BudgetSummaryModel>>{ data: summary, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchBudgets(
    filter: BudgetsFilter,
    orderBy?: string,
    skip?: number,
    take?: number
  ): Promise<ServiceResultModel<PaginatedListModel<BudgetModel>>> {
    try {
      const encodedOrderBy = orderBy ? encodeURIComponent(orderBy) : '';
      const requestUrl = `${this.financialsApiPath}/budgets/filter?orderBy=${encodedOrderBy}&skip=${skip ?? 0}&take=${take ?? ''}`;

      const response = await firstValueFrom(
        this.httpClient.post<BudgetModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BudgetsFilter>(filter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<BudgetModel> = {
        data: <BudgetModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}